/**
 * Hybrid storage utilities - uses localStorage + Firestore
 * Automatically syncs to cloud when user is authenticated
 */

import { auth } from '../firebase/config';
import {
  saveClientToFirestore,
  saveShiftToFirestore,
  saveInvoiceToFirestore,
  saveServiceAgreementToFirestore,
  saveSettingsToFirestore,
  deleteClientFromFirestore,
  deleteShiftFromFirestore,
  deleteInvoiceFromFirestore,
  deleteServiceAgreementFromFirestore,
  saveRecurringScheduleToFirestore,
  deleteRecurringScheduleFromFirestore,
  saveShiftNoteToFirestore,
  deleteShiftNoteFromFirestore
} from '../firebase/firestoreUtils';

const STORAGE_KEYS = {
  CLIENTS: 'ndis_clients',
  SHIFTS: 'ndis_shifts',
  SETTINGS: 'ndis_settings',
  INVOICES: 'ndis_invoices',
  SERVICE_AGREEMENTS: 'ndis_service_agreements',
  RECURRING_SCHEDULES: 'recurringSchedules',
  SHIFT_NOTES: 'ndis_shift_notes'
};

// Helper functions
function getFromStorage(key) {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error(`Error reading from localStorage: ${key}`, error);
    return null;
  }
}

function saveToStorage(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error(`Error saving to localStorage: ${key}`, error);
    return false;
  }
}

// Get current user ID
function getCurrentUserId() {
  return auth.currentUser?.uid;
}

// ==================== CLIENTS ====================

export function getClients() {
  return getFromStorage(STORAGE_KEYS.CLIENTS) || [];
}

export function saveClient(client) {
  const clients = getClients();
  const existingIndex = clients.findIndex(c => c.id === client.id);
  
  if (existingIndex >= 0) {
    clients[existingIndex] = client;
  } else {
    client.id = Date.now().toString();
    client.createdAt = new Date().toISOString();
    clients.push(client);
  }
  
  saveToStorage(STORAGE_KEYS.CLIENTS, clients);
  
  // Sync to Firestore if user is logged in
  const userId = getCurrentUserId();
  if (userId) {
    saveClientToFirestore(userId, client).catch(console.error);
  }
  
  return client;
}

export function deleteClient(clientId) {
  const clients = getClients();
  const filtered = clients.filter(c => c.id !== clientId);
  saveToStorage(STORAGE_KEYS.CLIENTS, filtered);
  
  // Delete from Firestore if user is logged in
  const userId = getCurrentUserId();
  if (userId) {
    deleteClientFromFirestore(userId, clientId).catch(console.error);
  }
}

export function getClient(clientId) {
  const clients = getClients();
  return clients.find(c => c.id === clientId) || null;
}

// ==================== SHIFTS ====================

export function getShifts() {
  const shifts = getFromStorage(STORAGE_KEYS.SHIFTS) || [];

  // Parse dates back to Date objects for proper formatting
  const parsedShifts = shifts.map(shift => ({
    ...shift,
    date: safeParseDateField(shift.date),
    createdAt: safeParseDateField(shift.createdAt),
    updatedAt: safeParseDateField(shift.updatedAt)
  }));

  // Debug logging (can be removed in production)
  if (shifts.length > 0 && shifts[0].date) {
    console.log('Shift date parsing - Original:', shifts[0].date, 'Parsed:', parsedShifts[0].date);
  }

  return parsedShifts;
}

export function saveShift(shift) {
  const shifts = getShifts();

  // Normalize dates to ISO strings to prevent Firestore from converting them to Timestamps
  const shiftToSave = {
    ...shift,
    date: dateToISOString(shift.date),
  };

  const existingIndex = shifts.findIndex(s => s.id === shiftToSave.id);

  if (existingIndex >= 0) {
    shifts[existingIndex] = shiftToSave;
  } else {
    shiftToSave.id = Date.now().toString();
    shiftToSave.createdAt = new Date().toISOString();
    shifts.push(shiftToSave);
  }

  saveToStorage(STORAGE_KEYS.SHIFTS, shifts);

  // Sync to Firestore if user is logged in
  const userId = getCurrentUserId();
  if (userId) {
    saveShiftToFirestore(userId, shiftToSave).catch(console.error);
  }

  return shiftToSave;
}

export function deleteShift(shiftId) {
  const shifts = getShifts();
  const filtered = shifts.filter(s => s.id !== shiftId);
  saveToStorage(STORAGE_KEYS.SHIFTS, filtered);
  
  // Delete from Firestore if user is logged in
  const userId = getCurrentUserId();
  if (userId) {
    deleteShiftFromFirestore(userId, shiftId).catch(console.error);
  }
}

export function getShift(shiftId) {
  const shifts = getShifts();
  return shifts.find(s => s.id === shiftId) || null;
}

export function getShiftsByClient(clientId) {
  const shifts = getShifts();
  return shifts.filter(s => s.clientId === clientId);
}

export function getShiftsByDateRange(startDate, endDate) {
  const shifts = getShifts();
  return shifts.filter(shift => {
    const shiftDate = new Date(shift.date);
    return shiftDate >= startDate && shiftDate <= endDate;
  });
}

// ==================== SETTINGS ====================

export function getSettings() {
  return getFromStorage(STORAGE_KEYS.SETTINGS) || {
    businessName: '',
    abn: '',
    address: '',
    suburb: '',
    state: '',
    postcode: '',
    country: 'AUSTRALIA',
    phone: '',
    email: '',
    website: '',
    bankName: '',
    bsb: '',
    accountNumber: '',
    invoicePrefix: 'INV-',
    invoiceCounter: 1,
    defaultInvoiceDueDays: 14
  };
}

export function saveSettings(settings) {
  saveToStorage(STORAGE_KEYS.SETTINGS, settings);
  
  // Sync to Firestore if user is logged in
  const userId = getCurrentUserId();
  if (userId) {
    saveSettingsToFirestore(userId, settings).catch(console.error);
  }
}

// ==================== INVOICES ====================

// Convert any date value to an ISO string (used before saving to prevent Firestore Timestamps)
function dateToISOString(dateValue) {
  if (!dateValue) return dateValue;
  if (dateValue instanceof Date) return dateValue.toISOString();
  if (typeof dateValue === 'object' && typeof dateValue.toDate === 'function') return dateValue.toDate().toISOString();
  if (typeof dateValue === 'object' && dateValue !== null && 'seconds' in dateValue) {
    return new Date(dateValue.seconds * 1000).toISOString();
  }
  return dateValue; // Already a string
}

// Helper function to safely parse dates
function safeParseDateField(dateValue) {
  if (!dateValue) return null;

  try {
    // Handle Firestore Timestamp objects (live SDK objects with toDate method)
    if (typeof dateValue === 'object' && typeof dateValue.toDate === 'function') {
      return dateValue.toDate();
    }

    // Handle serialized Firestore Timestamps ({seconds: N, nanoseconds: N})
    if (typeof dateValue === 'object' && dateValue !== null && !(dateValue instanceof Date) && 'seconds' in dateValue) {
      const date = new Date(dateValue.seconds * 1000);
      const time = date.getTime();
      return (isNaN(time) || time === 0) ? null : date;
    }

    // If it's already a Date object, check if it's valid
    if (dateValue instanceof Date) {
      const time = dateValue.getTime();
      return (isNaN(time) || time === 0) ? null : dateValue;
    }

    // If it's a string, check for common formats
    if (typeof dateValue === 'string') {
      // Handle HTML date format (YYYY-MM-DD)
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        const parsed = new Date(dateValue + 'T00:00:00');
        const time = parsed.getTime();
        return (isNaN(time) || time === 0) ? null : parsed;
      }
    }

    // If it's a string or number, try to parse it normally
    const parsed = new Date(dateValue);
    const time = parsed.getTime();

    if (isNaN(time) || time === 0) {
      console.warn('Invalid or epoch date encountered:', dateValue, 'parsed to:', parsed);
      return null;
    }

    return parsed;
  } catch (error) {
    console.error('Error parsing date:', error, dateValue);
    return null;
  }
}

export function getInvoices() {
  const invoices = getFromStorage(STORAGE_KEYS.INVOICES) || [];

  // Parse dates back to Date objects for proper formatting
  const parsedInvoices = invoices.map(invoice => {
    const parsed = {
      ...invoice,
      date: safeParseDateField(invoice.date),
      dueDate: safeParseDateField(invoice.dueDate),
      createdAt: safeParseDateField(invoice.createdAt),
      approvedAt: safeParseDateField(invoice.approvedAt),
      paidAt: safeParseDateField(invoice.paidAt)
    };

    // Debug logging for first invoice (can be removed in production)
    if (invoices.length > 0 && invoice === invoices[0]) {
      console.log('Invoice date parsing - Original:', invoice.date, 'Parsed:', parsed.date);
    }

    return parsed;
  });

  return parsedInvoices;
}

export function saveInvoice(invoice) {
  const invoices = getInvoices();

  // Normalize dates to ISO strings to prevent Firestore from converting them to Timestamps
  const invoiceToSave = {
    ...invoice,
    date: dateToISOString(invoice.date),
    dueDate: dateToISOString(invoice.dueDate),
  };

  const existingIndex = invoices.findIndex(inv => inv.id === invoiceToSave.id);

  if (existingIndex >= 0) {
    invoices[existingIndex] = invoiceToSave;
  } else {
    invoiceToSave.id = Date.now().toString();
    invoiceToSave.createdAt = new Date().toISOString();
    invoices.push(invoiceToSave);
  }

  saveToStorage(STORAGE_KEYS.INVOICES, invoices);

  // Sync to Firestore if user is logged in
  const userId = getCurrentUserId();
  if (userId) {
    saveInvoiceToFirestore(userId, invoiceToSave).catch(console.error);
  }

  return invoiceToSave;
}

export function getInvoicesByClient(clientId) {
  const invoices = getInvoices();
  return invoices
    .filter(inv => inv.client && inv.client.id === clientId)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
}

export function deleteInvoice(invoiceId) {
  const invoices = getInvoices();
  const filtered = invoices.filter(inv => inv.id !== invoiceId);
  saveToStorage(STORAGE_KEYS.INVOICES, filtered);
  
  // Delete from Firestore if user is logged in
  const userId = getCurrentUserId();
  if (userId) {
    deleteInvoiceFromFirestore(userId, invoiceId).catch(console.error);
  }
}

export function getNextInvoiceNumber() {
  const settings = getSettings();
  const invoiceNumber = `${settings.invoicePrefix}${String(settings.invoiceCounter).padStart(4, '0')}`;

  // Increment counter (convert to number first to avoid string concatenation)
  settings.invoiceCounter = Number(settings.invoiceCounter) + 1;
  saveSettings(settings);

  return invoiceNumber;
}

// ==================== SERVICE AGREEMENTS ====================

export function getServiceAgreements() {
  return getFromStorage(STORAGE_KEYS.SERVICE_AGREEMENTS) || [];
}

export function saveServiceAgreement(agreement) {
  const agreements = getServiceAgreements();
  const existingIndex = agreements.findIndex(a => a.id === agreement.id);

  if (existingIndex >= 0) {
    agreements[existingIndex] = agreement;
  } else {
    agreement.id = Date.now().toString();
    agreement.createdAt = new Date().toISOString();
    agreements.push(agreement);
  }

  saveToStorage(STORAGE_KEYS.SERVICE_AGREEMENTS, agreements);

  // Sync to Firestore if user is logged in
  const userId = getCurrentUserId();
  if (userId) {
    saveServiceAgreementToFirestore(userId, agreement).catch(console.error);
  }

  return agreement;
}

export function deleteServiceAgreement(agreementId) {
  const agreements = getServiceAgreements();
  const filtered = agreements.filter(a => a.id !== agreementId);
  saveToStorage(STORAGE_KEYS.SERVICE_AGREEMENTS, filtered);

  // Delete from Firestore if user is logged in
  const userId = getCurrentUserId();
  if (userId) {
    deleteServiceAgreementFromFirestore(userId, agreementId).catch(console.error);
  }
}

export function getServiceAgreement(agreementId) {
  const agreements = getServiceAgreements();
  return agreements.find(a => a.id === agreementId) || null;
}

export function getServiceAgreementsByClient(clientId) {
  const agreements = getServiceAgreements();
  return agreements
    .filter(agreement => agreement.participant && agreement.participant.id === clientId)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

export function getServiceAgreementsByStatus(status) {
  const agreements = getServiceAgreements();
  return agreements
    .filter(agreement => agreement.status === status)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

// ==================== RECURRING SCHEDULES ====================

export function getRecurringSchedules() {
  return getFromStorage(STORAGE_KEYS.RECURRING_SCHEDULES) || [];
}

export function saveRecurringSchedule(schedule) {
  const schedules = getRecurringSchedules();
  const existingIndex = schedules.findIndex(s => s.id === schedule.id);

  if (existingIndex >= 0) {
    schedules[existingIndex] = schedule;
  } else {
    schedule.id = Date.now().toString();
    schedule.createdAt = new Date().toISOString();
    schedules.push(schedule);
  }

  schedule.updatedAt = new Date().toISOString();
  saveToStorage(STORAGE_KEYS.RECURRING_SCHEDULES, schedules);

  // Sync to Firestore if user is logged in
  const userId = getCurrentUserId();
  if (userId) {
    saveRecurringScheduleToFirestore(userId, schedule).catch(console.error);
  }

  return schedule;
}

export function deleteRecurringSchedule(scheduleId) {
  const schedules = getRecurringSchedules();
  const filtered = schedules.filter(s => s.id !== scheduleId);
  saveToStorage(STORAGE_KEYS.RECURRING_SCHEDULES, filtered);

  const userId = getCurrentUserId();
  if (userId) {
    deleteRecurringScheduleFromFirestore(userId, scheduleId).catch(console.error);
  }
}

export function getRecurringScheduleByAgreement(agreementId) {
  const schedules = getRecurringSchedules();
  return schedules.find(s => s.serviceAgreementId === agreementId) || null;
}

// ==================== SHIFT NOTES ====================

export function getShiftNotes() {
  return getFromStorage(STORAGE_KEYS.SHIFT_NOTES) || [];
}

export function getShiftNotesByClient(clientId) {
  const notes = getShiftNotes();
  return notes.filter(n => n.clientId === clientId).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

export function getShiftNoteByShift(shiftId) {
  const notes = getShiftNotes();
  return notes.find(n => n.shiftId === shiftId) || null;
}

export function saveShiftNote(note) {
  const notes = getShiftNotes();
  const existingIndex = notes.findIndex(n => n.id === note.id);

  if (existingIndex >= 0) {
    notes[existingIndex] = { ...note, updatedAt: new Date().toISOString() };
  } else {
    note.id = Date.now().toString();
    note.createdAt = new Date().toISOString();
    note.updatedAt = new Date().toISOString();
    notes.push(note);
  }

  saveToStorage(STORAGE_KEYS.SHIFT_NOTES, notes);

  const userId = getCurrentUserId();
  if (userId) {
    saveShiftNoteToFirestore(userId, notes[existingIndex >= 0 ? existingIndex : notes.length - 1]).catch(console.error);
  }

  return notes[existingIndex >= 0 ? existingIndex : notes.length - 1];
}

export function deleteShiftNote(noteId) {
  const notes = getShiftNotes();
  const filtered = notes.filter(n => n.id !== noteId);
  saveToStorage(STORAGE_KEYS.SHIFT_NOTES, filtered);

  const userId = getCurrentUserId();
  if (userId) {
    deleteShiftNoteFromFirestore(userId, noteId).catch(console.error);
  }
}

// ==================== DATA MIGRATION ====================

export function loadDataFromFirestore(firestoreData) {
  // Load data from Firestore into localStorage
  // Account switching is handled in AuthContext (clears on user change)
  if (firestoreData.clients && firestoreData.clients.length > 0) {
    saveToStorage(STORAGE_KEYS.CLIENTS, firestoreData.clients);
  }

  if (firestoreData.shifts && firestoreData.shifts.length > 0) {
    saveToStorage(STORAGE_KEYS.SHIFTS, firestoreData.shifts);
  }

  if (firestoreData.invoices && firestoreData.invoices.length > 0) {
    saveToStorage(STORAGE_KEYS.INVOICES, firestoreData.invoices);
  }

  if (firestoreData.serviceAgreements && firestoreData.serviceAgreements.length > 0) {
    saveToStorage(STORAGE_KEYS.SERVICE_AGREEMENTS, firestoreData.serviceAgreements);
  }

  if (firestoreData.recurringSchedules && firestoreData.recurringSchedules.length > 0) {
    saveToStorage(STORAGE_KEYS.RECURRING_SCHEDULES, firestoreData.recurringSchedules);
  }

  if (firestoreData.settings) {
    saveToStorage(STORAGE_KEYS.SETTINGS, firestoreData.settings);
  }
}

// ==================== EXPORT/IMPORT ====================

export function exportAllData() {
  return {
    clients: getClients(),
    shifts: getShifts(),
    settings: getSettings(),
    invoices: getInvoices(),
    serviceAgreements: getServiceAgreements(),
    exportDate: new Date().toISOString()
  };
}

export function importAllData(data) {
  if (data.clients) saveToStorage(STORAGE_KEYS.CLIENTS, data.clients);
  if (data.shifts) saveToStorage(STORAGE_KEYS.SHIFTS, data.shifts);
  if (data.settings) saveToStorage(STORAGE_KEYS.SETTINGS, data.settings);
  if (data.invoices) saveToStorage(STORAGE_KEYS.INVOICES, data.invoices);
  if (data.serviceAgreements) saveToStorage(STORAGE_KEYS.SERVICE_AGREEMENTS, data.serviceAgreements);
}

export function clearAllData() {
  if (window.confirm('Are you sure you want to clear all data? This cannot be undone.')) {
    localStorage.removeItem(STORAGE_KEYS.CLIENTS);
    localStorage.removeItem(STORAGE_KEYS.SHIFTS);
    localStorage.removeItem(STORAGE_KEYS.INVOICES);
    localStorage.removeItem(STORAGE_KEYS.SERVICE_AGREEMENTS);
    return true;
  }
  return false;
}
